<template>
  <div class="signed-contract-download">
    <div v-if="loading" class="status-panel">
      <i class="fa fa-spinner fa-spin fa-2x text-primary mb-3"></i>
      <h4>Preparing your signed contract…</h4>
      <p class="text-muted">Your download should begin automatically in a moment.</p>
    </div>

    <div v-else-if="error" class="status-panel">
      <i class="fa fa-exclamation-triangle fa-2x text-danger mb-3"></i>
      <h4>Unable to download the signed contract</h4>
      <p class="text-muted">{{ error }}</p>
      <div class="mt-3">
        <button class="btn btn-primary btn-sm mr-2" @click="startDownload">
          <i class="fa fa-refresh"></i> Try again
        </button>
        <router-link
          class="btn btn-outline-secondary btn-sm"
          :to="{ name: 'LeadDetails', params: { id: leadId } }"
        >
          Go to lead
        </router-link>
      </div>
    </div>

    <div v-else class="status-panel">
      <i class="fa fa-check-circle fa-2x text-success mb-3"></i>
      <h4>Your signed contract is downloading</h4>
      <p class="text-muted">
        If the download did not start,
        <a href="#" @click.prevent="startDownload">click here</a>
        to try again.
      </p>
      <div class="mt-3">
        <router-link
          class="btn btn-outline-secondary btn-sm"
          :to="{ name: 'LeadDetails', params: { id: leadId } }"
        >
          Back to lead
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DownloadSignedContract',
  data() {
    return {
      loading: true,
      error: null,
    };
  },
  computed: {
    leadId() {
      return this.$route.params.id;
    },
    leadEventId() {
      return Number(this.$route.params.eventId);
    },
    contractId() {
      return Number(this.$route.params.contractId);
    },
  },
  created() {
    this.startDownload();
  },
  methods: {
    async startDownload() {
      this.loading = true;
      this.error = null;

      try {
        const attachment = await this.resolveSignedAttachment();
        if (! attachment) {
          this.error = 'No signed contract copy is available for this contract yet.';
          return;
        }

        // Always request a fresh pre-signed URL — S3 URLs expire after a
        // short window, so we must never reuse a previously fetched one.
        const res = await this.$store.dispatch('contracts/getDownloadUrl', {
          leadEventId: this.leadEventId,
          attachmentId: attachment.id,
        });
        const url = (res && (res.download_url || res.url)) || res;
        if (! url) {
          this.error = 'Could not generate a download link. Please try again.';
          return;
        }

        this.triggerBrowserDownload(url);
      } catch (e) {
        this.error = 'Something went wrong while preparing your download. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async resolveSignedAttachment() {
      const data = await this.$store.dispatch('contracts/fetchContracts', {
        leadEventId: this.leadEventId,
      });
      const list = (data && data.data) || [];
      let contract = null;
      for (const venueGroup of list) {
        if (venueGroup.active_contract
          && Number(venueGroup.active_contract.id) === this.contractId) {
          contract = venueGroup.active_contract;
          break;
        }
        const versions = Array.isArray(venueGroup.versions) ? venueGroup.versions : [];
        const match = versions.find(v => v && Number(v.id) === this.contractId);
        if (match) {
          contract = match;
          break;
        }
      }
      if (! contract) return null;

      const attachments = contract.attachments || [];
      return attachments.find(a => a.type === 'client_signed_copy')
        || attachments.find(a => a.type === 'vc_signed_copy')
        || null;
    },

    triggerBrowserDownload(url) {
      if (! url) return;
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.signed-contract-download {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem 1rem;
}

.status-panel {
  max-width: 480px;
  width: 100%;
  text-align: center;
  padding: 2rem;
  background: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}
</style>
